import { useCallback, useEffect, useRef } from "react";

/**
 * Returns a ref callback that fires `onVisible` exactly once, the first time
 * the attached element crosses the specified visibility threshold in the viewport.
 */
export const useVisibleOnceRef = (
  onVisible: () => void,
  options: { threshold: number } = { threshold: 0.5 }
): ((element: HTMLElement | null) => void) => {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const hasFiredRef = useRef(false);
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;
  const observerRef = useRef<IntersectionObserver | undefined>(undefined);

  const setRef = useCallback((element: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = undefined;

    if (!element || hasFiredRef.current) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        // `disconnect()` does not cancel notifications already queued by a
        // previous observer, so guard against a stale callback firing after a
        // new observer is attached to preserve the exactly-once contract.
        if (hasFiredRef.current) {
          return;
        }
        // The observer always fires an initial notification, so gate on the
        // ratio to ensure the element genuinely crossed the threshold.
        // Otherwise a partially-peeked element would fire on mount.
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= optionsRef.current.threshold
        ) {
          hasFiredRef.current = true;
          observerRef.current?.disconnect();
          onVisibleRef.current();
        }
      },
      optionsRef.current
    );
    observerRef.current.observe(element);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);
  return setRef;
};
